import { checkIfNameExists } from '_/utils/validation/nameValidator';
import { Modal, Input, Typography } from 'antd';
import React, { ReactElement, useState } from 'react';

interface AlarmGroup {
  name: string;
  [key: string]: any;
}

interface AddAlarmGroupModalProps {
  alarmGroupErrors?: string[];
  alarmGroups?: AlarmGroup[];
  handleCancel?: () => void;
  handleChange?: (name: string) => void;
  handleSave?: (name: string) => void;
  name?: string;
  open?: boolean;
  title?: string;
}

function AddAlarmGroupModal({
  alarmGroupErrors = [],
  alarmGroups = [],
  handleCancel,
  handleChange,
  handleSave,
  name = '',
  open = false,
  title,
}: AddAlarmGroupModalProps): ReactElement {
  const [isDirty, setIsDirty] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  const validateAlarmGroupName = (alarmGroupName: string): boolean => {
    if (checkIfNameExists(alarmGroups, alarmGroupName)) {
      return false;
    }

    return true;
  };

  const handleAlarmGroupConfirm = () => {
    // Clear previous error
    setLocalError(null);

    if (validateAlarmGroupName(name)) {
      if (handleSave) {
        handleSave(name);
      }
      return;
    }

    // Set local error instead of dispatching Redux action
    setLocalError(`An alarm group already exists with the name '${name}'`);
    setIsSaveDisabled(true);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (e.target.value) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }

    // Clear error when user starts typing
    setLocalError(null);

    if (name || isDirty) {
      if (handleChange) {
        handleChange(name);
      }
      setIsSaveDisabled(false);
    }
  };

  function onKeyPress(evt: React.KeyboardEvent) {
    // Enter is pressed
    if (evt.which === 13) {
      evt.preventDefault();
      evt.stopPropagation();
      handleAlarmGroupConfirm();
      // escape key is pressed
    } else if (evt.which === 27) {
      if (handleCancel) {
        handleCancel();
      }
    }
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      onOk={handleAlarmGroupConfirm}
      okText="Save"
      cancelText="Cancel"
      okButtonProps={{ disabled: !isDirty || isSaveDisabled }}
    >
      <div style={{ marginBottom: '8px' }}>
        <label htmlFor="name" style={{ display: 'block', marginBottom: '4px' }}>
          Alarm Group Name
        </label>
        <Input
          id="name"
          autoFocus
          value={name}
          onChange={handleTextChange}
          onKeyDown={onKeyPress}
          placeholder="Enter alarm group name"
        />
      </div>
      {localError && (
        <Typography.Text type="danger" style={{ display: 'block', margin: '2px 0' }}>
          {localError}
        </Typography.Text>
      )}
      {alarmGroupErrors.map((error, idx) => (
        <Typography.Text key={`error-${idx}`} type="danger" style={{ display: 'block', margin: '2px 0' }}>
          {error}
        </Typography.Text>
      ))}
    </Modal>
  );
}

export default AddAlarmGroupModal;
